import { supabase } from '../lib/supabase';

export const workforceManagementService = {
  async getActiveCompany() {
    const { data, error } = await supabase
      ?.from('company_settings')
      ?.select('*')
      ?.eq('is_active', true)
      ?.single();

    if (error) throw error;

    return {
      id: data?.id,
      companyName: data?.company_name,
      companyLegalName: data?.company_legal_name,
      companyAbbreviation: data?.company_abbreviation,
      companyLogoUrl: data?.company_logo_url,
      primaryColor: data?.primary_color,
      secondaryColor: data?.secondary_color,
      contactEmail: data?.contact_email,
      contactPhone: data?.contact_phone,
      companyAddress: data?.company_address,
      taxIdentificationNumber: data?.tax_identification_number,
      businessRegistrationNumber: data?.business_registration_number,
      industry: data?.industry,
      companySize: data?.company_size,
      timezone: data?.timezone,
      currency: data?.currency,
      dateFormat: data?.date_format,
      isActive: data?.is_active,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async getCompanySubscription(companyId) {
    const { data, error } = await supabase
      ?.from('company_subscriptions')
      ?.select('*')
      ?.eq('company_id', companyId)
      ?.single();

    // If no subscription exists, return null (clean slate)
    if (error && error?.code === 'PGRST116') {
      return null;
    }

    if (error) throw error;

    return {
      id: data?.id,
      companyId: data?.company_id,
      currentTier: data?.current_tier,
      subscriptionStatus: data?.subscription_status,
      activeEmployeeCount: data?.active_employee_count,
      monthlyPriceKina: data?.monthly_price_kina,
      billingPeriodStart: data?.billing_period_start,
      billingPeriodEnd: data?.billing_period_end,
      nextBillingDate: data?.next_billing_date,
      lastTierChangeDate: data?.last_tier_change_date,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  async getCompanyEmployees(companyId) {
    const { data, error } = await supabase
      ?.from('employees')
      ?.select('*')
      ?.eq('company_id', companyId)
      ?.order('created_at', { ascending: false });

    if (error) throw error;

    return data?.map(emp => ({
      id: emp?.id,
      employeeNumber: emp?.employee_number,
      firstName: emp?.first_name,
      lastName: emp?.last_name,
      email: emp?.email,
      phone: emp?.phone,
      dateOfBirth: emp?.date_of_birth,
      employmentType: emp?.employment_type,
      citizenStatus: emp?.citizen_status,
      department: emp?.department,
      position: emp?.position,
      hireDate: emp?.hire_date,
      terminationDate: emp?.termination_date,
      isActive: emp?.is_active,
      createdAt: emp?.created_at,
      updatedAt: emp?.updated_at
    })) || [];
  },

  async getSubscriptionTiers() {
    const { data, error } = await supabase
      ?.from('subscription_tiers')
      ?.select('*')
      ?.order('min_employees', { ascending: true });

    if (error) throw error;

    return data?.map(tier => ({
      id: tier?.id,
      tierName: tier?.tier_name,
      minEmployees: tier?.min_employees,
      maxEmployees: tier?.max_employees,
      monthlyPriceKina: tier?.monthly_price_kina,
      description: tier?.description,
      createdAt: tier?.created_at
    })) || [];
  },

  async getWorkforceAuditLogs(companyId, limit = 20) {
    const { data, error } = await supabase
      ?.from('workforce_audit_logs')
      ?.select('*')
      ?.eq('company_id', companyId)
      ?.order('created_at', { ascending: false })
      ?.limit(limit);

    if (error) throw error;

    return data?.map(log => ({
      id: log?.id,
      companyId: log?.company_id,
      actionType: log?.action_type,
      entityType: log?.entity_type,
      entityId: log?.entity_id,
      performedBy: log?.performed_by,
      employeeCountBefore: log?.employee_count_before,
      employeeCountAfter: log?.employee_count_after,
      tierBefore: log?.tier_before,
      tierAfter: log?.tier_after,
      billingImpactKina: log?.billing_impact_kina,
      changes: log?.changes,
      metadata: log?.metadata,
      createdAt: log?.created_at
    })) || [];
  },

  async addEmployee(companyId, employeeData) {
    const { data, error } = await supabase
      ?.from('employees')
      ?.insert([{
        company_id: companyId,
        employee_number: employeeData?.employeeNumber,
        first_name: employeeData?.firstName,
        last_name: employeeData?.lastName,
        email: employeeData?.email,
        phone: employeeData?.phone,
        date_of_birth: employeeData?.dateOfBirth,
        employment_type: employeeData?.employmentType,
        citizen_status: employeeData?.citizenStatus,
        department: employeeData?.department,
        position: employeeData?.position,
        hire_date: employeeData?.hireDate,
        is_active: true
      }])
      ?.select()
      ?.single();

    if (error) throw error;

    return data;
  },

  async removeEmployee(employeeId) {
    const { error } = await supabase
      ?.from('employees')
      ?.delete()
      ?.eq('id', employeeId);

    if (error) throw error;
  },

  async updateEmployeeStatus(employeeId, isActive) {
    const { error } = await supabase
      ?.from('employees')
      ?.update({ is_active: isActive, updated_at: new Date()?.toISOString() })
      ?.eq('id', employeeId);

    if (error) throw error;
  },

  async removeAllEmployees(companyId) {
    const { error } = await supabase
      ?.from('employees')
      ?.delete()
      ?.eq('company_id', companyId);

    if (error) throw error;
  }
};

export default workforceManagementService;
